import { css, html } from 'lit';
import { connect } from 'pwa-helpers';
import store, { waitForUser } from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { getCartItemFormat } from '../../common/utils/cart-util';
import { formattedAuthors } from '../../common/utils/util';
import { OptinOutData, OptInStatus } from './interface/inclusive-access.interface';
import '../alert/alert';
import { Modal } from '../modal/modal';
import { StoreState } from '../../store/store.interface';
import { fetchInclusiveAccessData } from './thunks/fetch-inclusive-access.thunk';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import { GENERIC_API_ERROR, loading } from '../../common/constants/api-constants';
import { MessageType } from '../alert/global-message.interface';
import { updateInclusiveAccessData } from './thunks/update-inclusive-access.thunk';
import { customElement, state } from 'lit/decorators.js';
import { checkForRedirectToLogin } from '../../common/utils/login-utils';

type InclusiveAccessModalAction = 'opt-in' | 'opt-out';

@customElement('mhecomm-inclusive-access-settings')
export class InclusiveAccessSetting extends connect(store)(BaseComponent) {
  static styles = [css``];

  @state()
  private optInData: OptinOutData[] = [];

  @state()
  private user: string | undefined;

  @state()
  private loading = false;

  @state()
  private errorMessage = '';

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
    waitForUser().then(() => store.dispatch(fetchInclusiveAccessData()));
  }

  stateChanged(state: StoreState) {
    checkForRedirectToLogin(state.userProfileState.userProfileResponse);
    this.loading = false;
    this.optInData = state.inclusiveAccessState.data;
    if (state.inclusiveAccessState.error) {
      if(state.inclusiveAccessState.error === 'No data found'){
        //when error is 404 show no data found
        this.optInData = [];
      } else {
        this.errorMessage = GENERIC_API_ERROR;
      }
    }
  }

  errorTemplate() {
    return html` <mhecomm-alert type=${MessageType.danger} message=${this.errorMessage}></mhecomm-alert> `;
  }

  renderComp() {
    return this.loading
      ? this._renderLoadingTemplate()
      : this.errorMessage
      ? this.errorTemplate()
      : this.optInData
      ? this.renderContent()
      : this._isLoading()
      ? this._renderLoadingTemplate()
      : html`<p>Please sign in to see inclusive access settings.</p>`;
  }

  renderContent() {
    const optInDataLength = this.optInData?.length;
    return optInDataLength === 0
      ? html`<div class="fs-3">
          <div class="alert alert-secondary alert-dismissible d-flex align-items-top" role="alert">
            <div>
              <h5 class="alert-heading">
                You don’t have any products with Inclusive Access options yet.
              </h5>
            </div>
          </div>
        </div>`
      : html`
          <div>
            ${this.user}
            <div class="container d-none d-lg-block mb-3">
              <div class="row fw-bold">
                <div class="col-lg-6 col-xl-6 container">Course Product</div>
                <div class="col-lg-2 col-xl-2 container">Opt-In Period</div>
                <div class="col-lg-2 col-xl-2 container">Last Updated</div>
                <div class="col-lg-2 col-xl-2 container">Selected Status</div>
              </div>
            </div>
            ${this.optInData?.map(
              (item, index) => html`
                    <ul class="list-group">
                          <div class="container">
                            <div class="row">
                              <div class="col-lg-6 col-xl-6 pb-3 pb-md-2">
                                <div class="container ps-0">
                                  <div class="row">
                                    <div class="col-6 col-md-4">
                                      <img
                                        alt=${item.productDescription}
                                        class="img-fluid"
                                        src=${getImageSrcUrl(item.coverImage ? item.coverImage : item.productIsbn)}
                                      />
                                    </div>
                                    <div class="col-12 col-md-8 pt-3 pt-md-0">
                                      <h3 class="h4 fs-large fw-semibold">
                                        ${item.productDescription}${
                item.edition && item.edition > 0 ? `, ${item.edition} Edition` : ''
              }
                                        <br />
                                      </h3>
                                      <div class="d-none d-sm-block">
                                        <span class="fw-bold"
                                          >${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span
                                        >
                                        ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                        ${
                                          item.isbn13
                                            ? html`
                                                |
                                                <span class="fw-bold">ISBN13:</span>
                                                ${item.isbn13}
                                              `
                                            : ''
                                        }
                                      </div>
                                      <div class="d-sm-none">
                                        <span class="fw-bold"
                                          >${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span
                                        >
                                        ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                        ${item.isbn13 ? '|' : ''}
                                      </div>
                                      ${
                                        item.isbn13
                                          ? html`
                                              <div class="d-sm-none">
                                                <span class="fw-bold">
                                                  ISBN13:
                                                </span>
                                                ${item.isbn13}
                                              </div>
                                            `
                                          : ''
                                      }
                                      ${
                                        item.authors
                                          ? html` <div>
                                              <span class="fw-bold">By:</span>
                                              ${item.authors.map((item) => formattedAuthors(item))}
                                            </div>`
                                          : ''
                                      }
                                      ${item.copyrightYear ? html` <div>©${item.copyrightYear}</div> ` : ''}
                                      ${
                                        item.formatDescription
                                          ? html`
                                              <div>
                                                <span class="fw-bold">Format:</span>
                                                ${getCartItemFormat(
                                                  item.formatDescription,
                                                  item.formatCode,
                                                  item.virtual
                                                )}
                                              </div>
                                            `
                                          : ''
                                      }
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="col-lg-2 col-xl-2 pb-md-2">
                                <span class="fw-bold d-lg-none">Opt-In Period</span>
                                <span
                                  class="badge mt-2 ${this._getBadgeStyleFromLineStatusItem(
                                    item
                                  )} float-end float-lg-none"
                                >
                                  ${this._getUiStatus(item)}
                                </span>
                              </div>
                              <div class="col-lg-2 col-xl-2 pb-md-2">
                                <span class="fw-bold d-lg-none">Last Updated</span
                                ><span class="float-end float-lg-none">${this.formatDate(item.lastUpdatedDate)}</span>
                              </div>
                              <div class="col-lg-2 col-xl-2 pb-md-2">
                                <span class="fw-bold d-lg-none">Selected Status</span>
                                <span class="float-end float-lg-none">
                                  <div class="form-check form-switch">
                                    <input
                                      class="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id="flexSwitchCheckDefault"
                                      data-automation-id="optinout-${index}"
                                      ?checked=${item.isUserOptedIn}
                                      @change="${(event) => this.openConfirmationModal(item, event)}"
                                      ?disabled=${item.optInStatus === 'closed' || !item.userSectionOptInOutMetaData}
                                    />
                                    <label class="form-check-label" for="flexSwitchCheckDefault">
                                      ${item.isUserOptedIn ? 'Opted In' : 'Opted out'}
                                    </label>
                                  </div>
                                </span>
                              </div>
                            </div>
                          </div>
                          <hr />
                    </ul>
                  </div>
                </div>
              `
            )}
          </div>
        `;
  }

  _isLoading() {
    if (window.Mhecomm_Editor) {
      return false;
    }
    this.loading = true;
    return this.loading;
  }

  _renderLoadingTemplate() {
    return html`
      <span class="visually-hidden">Loading Inclusive Access</span>
      ${loading({ width: '100%', height: '5rem', m: '' })}
      <hr />
      <div class="loading-container mb-4">
        ${loading({ width: '100%', height: '12rem' })}
        <div>
          ${loading({ height: '1.25rem', width: '70%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '40%' })} ${loading({ height: '1.25rem', width: '20%' })}
        </div>
        <div>
          ${loading({ height: '1.25rem', width: '100%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '50%' })}
        </div>
      </div>
    `;
  }

  _getBadgeStyleFromLineStatusItem(optInData): string {
    switch (optInData.optInStatus) {
      case OptInStatus.OPEN:
        return 'bg-info text-dark';
      case OptInStatus.CLOSED:
        return 'bg-danger';
      default:
        return '';
    }
  }

  _getUiStatus(optInData): string {
    switch (optInData.optInStatus) {
      case OptInStatus.OPEN:
        return optInData.expiryDate ? 'Open Until ' + this.formatDate(optInData.expiryDate) : 'Open';
      case OptInStatus.CLOSED:
        return 'Closed';
      default:
        return '';
    }
  }

  formatDate(timestamp) {
    const date = new Date(timestamp);

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear() % 100;

    const formattedDate = `${month}/${day}/${year}`;
    return formattedDate;
  }

  private openConfirmationModal(selectedItem, _event: InputEvent) {
    this.loading = true;
    const action: InclusiveAccessModalAction = selectedItem.isUserOptedIn ? 'opt-out' : 'opt-in';
    const isOptOut = action === 'opt-out';
    const modal: Modal = new Modal({
      header: isOptOut
        ? 'Are you sure you want to opt-out of Inclusive Access for this item?'
        : 'With Inclusive Access you get:',
      headerClassName: isOptOut ? 'fw-bold text-dark' : undefined,
      isHeaderTextCapitalize: false,
      body: { template: this._getModalTemplate(selectedItem, action) },
      primaryButton: { label: 'Confirm', className: isOptOut ? 'btn btn-outline-danger' : 'btn btn-danger' },
      primaryButtonFirst: isOptOut,
      secondaryButton: { label: 'Cancel', className: isOptOut ? 'btn btn-danger' : 'btn btn-outline-danger' },
      modalContext: { componentRef: `confirmation-modal` },
    });
    modal.addEventListener('mhecomm-modal-confirmed', async () => {
      const data = {
        dealId: selectedItem.userSectionOptInOutMetaData.dealId?.toString(),
        sectionXid: selectedItem.userSectionOptInOutMetaData.sectionXid,
        isbn: selectedItem.userSectionOptInOutMetaData.isbn,
        expiryDate: selectedItem.userSectionOptInOutMetaData.expiryDate,
        sectionId: selectedItem.userSectionOptInOutMetaData.sectionId,
        sectionUuid: selectedItem.userSectionOptInOutMetaData.sectionUuid,
        organizationXid: selectedItem.userSectionOptInOutMetaData.organizationXid,
        instructorXid: selectedItem.userSectionOptInOutMetaData.instructorXid,
        action: selectedItem.isUserOptedIn ? 'opt-out' : 'opt-in',
        calledFrom: 'ecomm-ui',
        platform: selectedItem.userSectionOptInOutMetaData.platform
      };
      this.loading = true;
      store.dispatch(updateInclusiveAccessData(data));
      modal.close();
    });
    modal.addEventListener('confirmation-modal-modal-close', () => {
      this.loading = false;
    })
    modal.open();
  }

  private _getModalTemplate(item, action: InclusiveAccessModalAction) {
    if (action === 'opt-in') {
      return html`
        ${this._getNewModalStyles()}
        <div id="allPrograms" class="fs-6 overflow-auto ps-1">
          <div class="ia-modal-info-panel--benefits bg-light border rounded fs-small lh-base p-3 mb-4">
            <ul>
              <li>A discounted price negotiated by your school for your required course materials</li>
              <li>
                Course materials billed to your student account alongside tuition and fees, requiring no upfront
                payment
              </li>
            </ul>
            <p>
              If you previously purchased these required course materials individually, visit
              <a href="/account/order-history.html">My Account Settings &gt; Order History</a>
              to request a refund.
            </p>
            <p class="mb-0">
              If you have further questions, review the
              <a href="/return-policy.html">McGraw Hill Refund Policy</a>.
            </p>
          </div>
          ${this._getCompactModalProductTemplate(item)}
        </div>
      `;
    }

    return html`
      ${this._getNewModalStyles()}
      <div id="allPrograms" class="fs-6 overflow-auto ps-1">
        <div class="ia-modal-info-panel--loss bg-light border rounded p-3 mb-4">
          <p>If you opt-out, <span class="fw-bold">you will lose</span>:</p>
          <ul>
            <li>Immediate access to your required McGraw Hill course materials</li>
            <li>Discounted pricing through your school</li>
          </ul>
          <p>What stays the same:</p>
          <ul class="mb-0">
            <li>Your progress in course materials will be saved</li>
            <li>You can regain access by purchasing course materials individually at full retail price</li>
          </ul>
        </div>
        ${this._getCompactModalProductTemplate(item)}
      </div>
    `;
  }

  private _getNewModalStyles() {
    return html`
      <style>
        .ia-modal-info-panel--loss {
          color: #4f4f4f;
        }

        .ia-modal-product__image--desktop {
          width: 4.75rem;
        }

        .ia-modal-product__image--mobile {
          width: 4rem;
        }

        .ia-modal-product__details {
          color: #1f1f1f;
          line-height: 1.4;
        }
      </style>
    `;
  }

  private _getCompactModalProductTemplate(item) {
    const imageSrc = getImageSrcUrl(item.coverImage ? item.coverImage : item.productIsbn);

    return html`
      <div class="ia-modal-product d-flex align-items-stretch gap-3">
        <img
          alt=${item.productDescription}
          class="ia-modal-product__image--mobile d-block d-sm-none flex-shrink-0 img-fluid"
          src=${imageSrc} />
        <img
          alt=${item.productDescription}
          class="ia-modal-product__image--desktop d-none d-sm-block flex-shrink-0 img-fluid me-1"
          src=${imageSrc} />
        <div
          class="ia-modal-product__details align-self-stretch d-flex flex-column flex-grow-1 fs-small justify-content-between">
          <h3 class="fw-bold fs-6 lh-sm text-black mb-0">
            ${item.productDescription}${item.edition && item.edition > 0 && !item.isEvergreen
              ? `, ${item.edition} Edition`
              : ''}
          </h3>
          <div>
            <span class="fw-bold">${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span>
            ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
            ${item.isbn13
              ? html`
                  |
                  <span class="fw-bold">ISBN13:</span>
                  ${item.isbn13}
                `
              : ''}
          </div>
          ${item.formatDescription
            ? html`
                <div>
                  <span class="fw-bold">Format:</span>
                  ${getCartItemFormat(item.formatDescription, item.formatCode, item.virtual)}
                </div>
              `
            : ''}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-inclusive-access-settings': InclusiveAccessSetting;
  }
}
